import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Check, Wallet, ArrowRight } from 'lucide-react';

const PricingCard = ({ packageData, onPurchase }) => {
    const { name, credits, price, discounted, discountedPrice, features, popular } = packageData;

    const formattedCredits = credits.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    const formattedPrice = price.toFixed(2);

    return (
        <Card className={`relative transform transition-all duration-300 hover:scale-105
            ${popular ?
                'bg-gradient-to-br from-white via-blue-50/50 to-blue-100/30 dark:from-gray-900 dark:via-blue-900/20 dark:to-blue-950/10 border-2 border-blue-500/50 dark:border-blue-400/30 shadow-lg shadow-blue-200/50 dark:shadow-blue-500/10' :
                'hover:border-blue-400 dark:hover:border-blue-500/30 hover:shadow-lg hover:shadow-gray-200/50 dark:hover:shadow-blue-500/5 dark:bg-gradient-to-br dark:from-gray-900 dark:to-gray-900/80'}`
        }>
            {popular && (
                <div className="absolute -top-3 left-0 right-0 flex justify-center">
                    <Badge className="bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 dark:from-blue-500 dark:via-blue-400 dark:to-blue-500 shadow-md">
                        <Sparkles className="w-3 h-3 mr-1" />
                        <span className="text-sm">Most Popular</span>
                    </Badge>
                </div>
            )}

            <CardHeader className="pb-4">
                <CardTitle className={`text-xl font-semibold ${popular ?
                    'bg-gradient-to-r from-blue-700 via-blue-600 to-blue-500 dark:from-blue-400 dark:via-blue-300 dark:to-blue-400' :
                    'bg-gradient-to-r from-gray-900 via-gray-800 to-gray-700 dark:from-gray-300 dark:via-gray-200 dark:to-gray-300'
                    } bg-clip-text text-transparent`}>
                    {name}
                </CardTitle>
                <CardDescription className="flex items-center gap-2 mt-4">
                    {discounted ? (
                        <div className="flex flex-col">
                            <div className="flex items-center gap-3">
                                <span className="text-3xl font-medium text-blue-600 dark:text-blue-300">
                                    ${discountedPrice.toFixed(2)}
                                </span>
                                <div className="flex flex-col">
                                    <span className="text-lg text-gray-400 dark:text-gray-500 line-through">
                                        ${formattedPrice}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <span className="text-3xl font-medium text-blue-600 dark:text-blue-300">
                            ${formattedPrice}
                        </span>
                    )}
                </CardDescription>
            </CardHeader>

            <CardContent className="space-y-8">
                <div className={`flex items-center p-3 rounded-xl backdrop-blur-sm
                    ${popular ?
                        'bg-gradient-to-r from-blue-100/50 to-white dark:from-blue-900/20 dark:to-blue-900/10' :
                        'bg-gradient-to-r from-gray-100/50 to-white dark:from-gray-800/20 dark:to-gray-800/10'
                    }`}>
                    <Wallet className={`w-5 h-5 ${popular ? 'text-blue-500 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'} mr-3`} />
                    <span className={`text-base font-medium ${popular ?
                        'bg-gradient-to-r from-blue-700 via-blue-600 to-blue-500 dark:from-blue-400 dark:via-blue-300 dark:to-blue-400' :
                        'bg-gradient-to-r from-gray-900 via-gray-800 to-gray-700 dark:from-gray-300 dark:via-gray-200 dark:to-gray-300'
                        } bg-clip-text text-transparent`}>
                        {formattedCredits} Credits
                    </span>
                </div>

                <ul className="space-y-4">
                    {features.map((feature, index) => (
                        <li key={index} className="flex items-start group">
                            <div className="rounded-full p-1 bg-gradient-to-br from-green-100 to-green-50 dark:from-green-500/20 dark:to-green-500/10 mr-3 group-hover:shadow-sm transition-all duration-300">
                                <Check className="w-3 h-3 text-green-500 dark:text-green-400" />
                            </div>
                            <span className="text-sm text-gray-600 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                {feature}
                            </span>
                        </li>
                    ))}
                </ul>
            </CardContent>

            <CardFooter className="">
                <Button
                    className={`w-full group relative overflow-hidden transition-all duration-300
                        ${popular ?
                            'bg-gradient-to-r from-blue-700 via-blue-600 to-blue-500 hover:from-blue-600 hover:via-blue-500 hover:to-blue-400 dark:from-blue-600 dark:via-blue-500 dark:to-blue-600 dark:hover:from-blue-500 dark:hover:via-blue-400 dark:hover:to-blue-500 shadow-md shadow-blue-500/20 dark:shadow-blue-500/10' :
                            'bg-gradient-to-r from-gray-900 via-gray-800 to-gray-700 hover:from-gray-800 hover:via-gray-700 hover:to-gray-600 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 dark:hover:from-gray-600 dark:hover:via-gray-500 dark:hover:to-gray-600 shadow-md shadow-gray-500/10 dark:shadow-gray-500/5'
                        }`}
                    onClick={() => onPurchase(packageData)}
                >
                    <span className="flex items-center justify-center gap-2 py-1 text-sm font-medium">
                        Purchase Now
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                </Button>
            </CardFooter>
        </Card>
    );
};

export default PricingCard;